import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from "../components/ui/Button";
import { Badge } from "../components/ui/Badge";
import { Card, CardContent } from "../components/ui/Card";
import { 
  ArrowLeft, 
  Printer, 
  Trash2, 
  Edit3, 
  Mail, 
  Phone, 
  CreditCard,
  X
} from "lucide-react";
import { ordersService } from '../services/orders';
import OrderInfoEditModal from '../components/OrderInfoEditModal';
import { toast } from 'sonner';

export default function VendaDetalhesPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCancelOrderModalOpen, setIsCancelOrderModalOpen] = useState(false);
  const [isCancelProductModalOpen, setIsCancelProductModalOpen] = useState(false);
  const [cancelProductData, setCancelProductData] = useState<{ id: number; name: string } | null>(null);

  const fetchOrderDetails = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await ordersService.getOrderById(id);
      if (response.success) {
        setOrder(response.data);
      }
    } catch (error) {
      console.error("Erro ao carregar detalhes da venda:", error);
      toast.error("Erro ao carregar detalhes da venda.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrderDetails();
  }, [id]);

  const handlePrint = () => {
    window.print();
  };

  const handleCancelOrder = () => {
    if (!order) return;
    setIsCancelOrderModalOpen(true);
  };

  const confirmCancelOrder = async () => {
    if (!order) return;

    try {
      const response = await ordersService.cancelOrder(order.id);
      if (response.success) {
        toast.success("Venda cancelada com sucesso!");
        setIsCancelOrderModalOpen(false);
        fetchOrderDetails();
      }
    } catch (error: any) {
      console.error("Erro ao cancelar venda:", error);
      toast.error("Erro ao cancelar venda: " + (error.response?.data?.message || error.message));
    }
  };

  const handleCancelProduct = (productId: number, productName: string) => {
    if (!order) return;

    // Se for o único produto ativo da venda, abre o modal de cancelamento da ordem
    const activeProducts = (order.products || []).filter((p: any) => !p.canceled_at && !p.deleted_at);
    if (activeProducts.length === 1) {
      setIsCancelOrderModalOpen(true);
      return;
    }

    setCancelProductData({ id: productId, name: productName });
    setIsCancelProductModalOpen(true);
  };

  const confirmCancelProduct = async () => {
    if (!order || !cancelProductData) return;

    try {
      // Retorna sempre o produto ao estoque (returnToStore = true)
      const response = await ordersService.cancelProduct(order.id, cancelProductData.id, true);
      if (response.success) {
        toast.success("Produto removido/cancelado da venda!");
        setIsCancelProductModalOpen(false);
        setCancelProductData(null);
        fetchOrderDetails();
      }
    } catch (error: any) {
      console.error("Erro ao cancelar produto:", error);
      toast.error("Erro ao cancelar produto: " + (error.response?.data?.message || error.message));
    }
  };

  if (loading) {
    return <div className="p-8 text-center text-[#68686A]">Carregando detalhes do pedido...</div>;
  }

  if (!order) {
    return (
      <div className="p-8 text-center text-[#68686A]">
        <p className="mb-4">Pedido não encontrado.</p>
        <Button onClick={() => navigate('/vendas')} className="bg-[#D4C070]">
          Voltar para Vendas
        </Button>
      </div>
    );
  }

  // Verifica se o pedido já está cancelado
  const isOrderCanceled = Number(order?.status?.id ?? order?.status_id) === 7 || order?.status?.name === 'Cancelado';

  return (
    <main className="flex-1 p-6 bg-gray-50 text-[#68686A]">
      {/* Botões de Ação */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4 no-print">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/vendas')}
          className="text-[#68686A] hover:text-[#D4C070]"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar para Vendas
        </Button>

        <div className="flex items-center gap-2">
          {!isOrderCanceled && (
            <Button
              variant="outline"
              onClick={handleCancelOrder}
              className="border-rose-200 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Cancelar Venda
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => setIsEditModalOpen(true)}
            className="border-gray-300 hover:bg-[#EEF1F2]"
          >
            <Edit3 className="h-4 w-4 mr-2" />
            Editar Pagamentos
          </Button>
          <Button
            onClick={handlePrint}
            className="bg-[#D4C070] hover:bg-[#849D70] text-white"
          >
            <Printer className="h-4 w-4 mr-2" />
            Imprimir Fatura
          </Button>
        </div>
      </div>

      {/* CSS específico para Impressão */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          #invoice-container,
          #invoice-container * {
            visibility: visible;
          }
          #invoice-container {
            position: absolute;
            left: 0;
            top: 0;
            width: 210mm !important;
            max-width: 210mm !important;
            min-width: 210mm !important;
            margin: 0 !important;
            padding: 15mm !important;
            box-shadow: none !important;
            border: none !important;
            background: white !important;
          }
          .no-print {
            display: none !important;
          }
          tr {
            page-break-inside: avoid;
          }
        }
        @page {
          size: A4 portrait;
          margin: 0;
        }
      `}</style>

      {/* Card da Fatura */}
      <Card id="invoice-container" className="border border-gray-200 bg-white p-8 max-w-4xl mx-auto shadow-sm">
        <CardContent className="p-0 space-y-8">
          
          {/* Header da Fatura */}
          <div className="flex justify-between items-start gap-4">
            <div>
              <img
                src="https://assets.foradocloset.com/img/logo.png"
                alt="Fora do Closet Logo"
                className="w-44 h-auto object-contain"
              />
            </div>
            <div className="text-right">
              <h1 className="text-3xl font-extrabold text-gray-900 leading-tight">FATURA</h1>
              <div className="mt-4 space-y-1 text-sm">
                <p className="font-semibold text-gray-900">Pedido nº {order.id}</p>
                <div>
                  <p className="text-gray-400">Data do Pedido:</p>
                  <p className="font-medium text-gray-800">{order.created_at.timestamp}</p>
                </div>
                <div className="pt-2">
                  <Badge 
                    className={
                      order.status.id === 5 ? 'bg-emerald-100 text-emerald-800 border-emerald-200' :
                      order.status.id === 7 ? 'bg-rose-100 text-rose-800 border-rose-200' :
                      'bg-amber-100 text-amber-800 border-amber-200'
                    }
                    variant="outline"
                  >
                    {order.status.name}
                  </Badge>
                </div>
              </div>
            </div>
          </div>

          {/* Dados do Cliente vs Empresa */}
          <div className="grid grid-cols-1 md:grid-cols-2 print:grid-cols-2 gap-8 border-t border-b py-6 text-sm">
            {/* Receptor/Cliente */}
            <div>
              <h5 className="font-bold text-gray-900 mb-3 text-base">Cliente</h5>
              {order.customer?.id ? (
                <div className="space-y-2">
                  <p className="font-semibold text-gray-800">
                    {order.customer.name} {order.customer.document && `| CPF: ${order.customer.document}`}
                  </p>
                  
                  {order.customer.address ? (
                    <div className="text-gray-600 leading-relaxed">
                      <p>{order.customer.address.address}, {order.customer.address.number}</p>
                      {order.customer.address.complement && <p>Compl: {order.customer.address.complement}</p>}
                      <p>{order.customer.address.neighborhood}</p>
                      <p>{order.customer.address.city} - {order.customer.address.state}</p>
                      <p className="font-mono text-xs mt-1">{order.customer.address.zip}</p>
                    </div>
                  ) : (
                    <p className="text-gray-400 italic">Endereço não cadastrado</p>
                  )}

                  <div className="pt-2 space-y-1.5">
                    <p className="flex items-center text-gray-600 break-all">
                      <Mail className="h-4 w-4 mr-2 text-gray-400" />
                      <span>{order.customer.email}</span>
                    </p>
                    {order.customer.phone && (
                      <p className="flex items-center text-gray-600">
                        <Phone className="h-4 w-4 mr-2 text-gray-400" />
                        <span>{order.customer.phone}</span>
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <p className="text-gray-400 italic">Dados de cliente indisponíveis</p>
              )}
            </div>

            {/* Emissor/Fora do Closet */}
            <div className="md:text-right print:text-right">
              <h5 className="font-bold text-gray-900 mb-3 text-base">Fora do Closet</h5>
              <div className="space-y-2">
                <div className="text-gray-600 leading-relaxed">
                  <p>Rua Afonso Braz, 579 - Conj 31</p>
                  <p>Vila Nova Conceição, São Paulo - SP</p>
                  <p className="font-mono text-xs mt-1">04511-011</p>
                </div>
                <div className="pt-2 space-y-1.5 md:flex md:flex-col md:items-end print:flex print:flex-col print:items-end">
                  <p className="flex items-center text-gray-600">
                    <Mail className="h-4 w-4 mr-2 text-gray-400 md:order-2 md:mr-0 md:ml-2 print:order-2 print:mr-0 print:ml-2" />
                    <span>contato@foradocloset.com.br</span>
                  </p>
                  <p className="flex items-center text-gray-600">
                    <Phone className="h-4 w-4 mr-2 text-gray-400 md:order-2 md:mr-0 md:ml-2 print:order-2 print:mr-0 print:ml-2" />
                    <span>(11) 3053-3071</span>
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Tabela de Produtos */}
          <div>
            <div className="mb-2 flex items-center text-sm font-semibold text-gray-900">
              <CreditCard className="h-4 w-4 mr-2 text-gray-400" />
              <span>Pago com: {order.payment.gateway}</span>
            </div>

            <div className="border rounded overflow-x-auto w-full">
              <table className="w-full text-left text-sm border-collapse">
                <thead>
                  <tr className="bg-gray-50 border-b font-semibold text-gray-700">
                    <th className="p-3 w-10 no-print"></th>
                    <th className="p-3">Produto</th>
                    <th className="p-3 w-28">Código (SKU)</th>
                    <th className="p-3 w-20 text-center">Quant.</th>
                    <th className="p-3 w-32 text-right">Valor</th>
                    <th className="p-3 w-28 text-center">Forma</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {order.products && order.products.map((item: any, idx: number) => {
                    const isCanceled = !!item.canceled_at || !!item.deleted_at || isOrderCanceled;

                    return (
                      <tr key={idx} className={`hover:bg-gray-50/30 ${isCanceled ? 'bg-rose-50/20 opacity-60 line-through' : ''}`}>
                        {/* Botão de Cancelar Item */}
                        <td className="p-3 text-center no-print">
                          {!isCanceled && !isOrderCanceled && (
                            <button
                              onClick={() => handleCancelProduct(item.id, item.name)}
                              title="Remover produto da venda"
                              className="text-gray-400 hover:text-rose-600 transition-colors p-1 rounded-full hover:bg-rose-50"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          )}
                        </td>
                        
                        {/* Nome do Produto */}
                        <td className="p-3 max-w-xs md:max-w-md">
                          <div className="break-words whitespace-normal">
                            <p className="font-semibold text-gray-900 break-words whitespace-normal">{item.name}</p>
                            <p className="text-xs text-gray-400 mt-0.5 break-words whitespace-normal">{item.description}</p>
                          </div>
                        </td>
                        
                        {/* SKU */}
                        <td className="p-3 font-mono text-xs break-all">{item.sku}</td>
                        
                        {/* Quantidade */}
                        <td className="p-3 text-center">1</td>
                        
                        {/* Preço */}
                        <td className="p-3 text-right font-medium">
                          {item.price.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                        </td>
                        
                        {/* Forma */}
                        <td className="p-3 text-center text-xs">
                          {item.parceled === 'parceled' ? (
                            <Badge variant="secondary" className="bg-purple-50 text-purple-700 border-purple-100">Parcelado</Badge>
                          ) : (
                            <Badge variant="secondary" className="bg-blue-50 text-blue-700 border-blue-100">À Vista</Badge>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Totais e Pagamentos Parciais */}
          <div className="flex flex-col md:flex-row justify-between items-start gap-8 pt-4">
            
            {/* Pagamentos parciais detalhados */}
            <div className="w-full md:w-1/2 space-y-2">
              <h6 className="font-bold text-gray-900 text-sm">Detalhamento dos Pagamentos</h6>
              <div className="divide-y border rounded bg-gray-50/50 text-xs">
                {Number(order.payment.payment1?.value || 0) > 0 && (
                  <div className="flex justify-between p-2.5">
                    <span className="text-gray-500">Pagamento 1 ({order.payment.payment1.type})</span>
                    <span className="font-semibold text-gray-900">
                      {order.payment.payment1.value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                    </span>
                  </div>
                )}
                {Number(order.payment.payment2?.value || 0) > 0 && (
                  <div className="flex justify-between p-2.5">
                    <span className="text-gray-500">Pagamento 2 ({order.payment.payment2.type})</span>
                    <span className="font-semibold text-gray-900">
                      {order.payment.payment2.value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                    </span>
                  </div>
                )}
                {Number(order.payment.payment3?.value || 0) > 0 && (
                  <div className="flex justify-between p-2.5">
                    <span className="text-gray-500">Pagamento 3 ({order.payment.payment3.type})</span>
                    <span className="font-semibold text-gray-900">
                      {order.payment.payment3.value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                    </span>
                  </div>
                )}
                {Number(order.payment.payment4?.value || 0) > 0 && (
                  <div className="flex justify-between p-2.5">
                    <span className="text-gray-500">Pagamento 4 ({order.payment.payment4.type})</span>
                    <span className="font-semibold text-gray-900">
                      {order.payment.payment4.value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                    </span>
                  </div>
                )}
                {!Number(order.payment.payment1?.value || 0) && (
                  <p className="p-3 text-gray-400 italic">Nenhum pagamento parcial cadastrado.</p>
                )}
              </div>
            </div>

            {/* Resumo de Totais */}
            <div className="w-full md:w-1/2 space-y-2 text-sm">
              <div className="flex justify-between py-1.5">
                <span className="text-gray-500">SUBTOTAL</span>
                <span className="font-semibold text-gray-900">
                  {order.subtotal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </span>
              </div>
              {Number(order.discount || 0) > 0 && (
                <div className="flex justify-between py-1.5 text-emerald-600 font-semibold">
                  <span>DESCONTO {order.discount_code ? `(${order.discount_code})` : ''}</span>
                  <span>
                    - {Number(order.discount).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                  </span>
                </div>
              )}
              <div className="flex justify-between py-1.5">
                <span className="text-gray-500">FRETE</span>
                <span className="font-semibold text-gray-900">
                  {(order.shipping_total || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </span>
              </div>
              <div className="flex justify-between py-2 border-t border-b border-gray-200 font-bold text-base">
                <span className="text-gray-900">TOTAL</span>
                <span className="text-[#849D70] font-extrabold">
                  {order.total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </span>
              </div>
            </div>

          </div>

        </CardContent>
      </Card>

      {/* Modal de Edição de Informações */}
      <OrderInfoEditModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        orderInfo={order}
        onSuccess={fetchOrderDetails}
      />

      {/* Modal de Confirmação de Cancelamento de Venda */}
      {isCancelOrderModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-[100] bg-black/20 backdrop-blur-md">
          <div className="bg-white rounded-lg p-6 w-full max-w-md relative border border-gray-200 shadow-xl text-black">
            <button 
              onClick={() => setIsCancelOrderModalOpen(false)} 
              className="absolute top-3 right-3 text-white bg-[#D4C070] hover:bg-[#D4C070] rounded-full p-1 z-10"
            >
              <X className="h-5 w-5" />
            </button>

            <h3 className="text-lg font-semibold mb-4 text-gray-900">Cancelar Venda</h3>
            <p className="text-sm text-gray-600 mb-6">
              Tem certeza que deseja cancelar esta venda? Esta ação devolverá os produtos ao estoque.
            </p>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsCancelOrderModalOpen(false)}>
                Voltar
              </Button>
              <Button 
                onClick={confirmCancelOrder} 
                className="bg-rose-600 hover:bg-rose-700 text-white"
              >
                Confirmar Cancelamento
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmação de Remoção de Produto */}
      {isCancelProductModalOpen && cancelProductData && (
        <div className="fixed inset-0 flex items-center justify-center z-[100] bg-black/20 backdrop-blur-md">
          <div className="bg-white rounded-lg p-6 w-full max-w-md relative border border-gray-200 shadow-xl text-black">
            <button 
              onClick={() => {
                setIsCancelProductModalOpen(false);
                setCancelProductData(null);
              }} 
              className="absolute top-3 right-3 text-white bg-[#D4C070] hover:bg-[#D4C070] rounded-full p-1 z-10"
            >
              <X className="h-5 w-5" />
            </button>

            <h3 className="text-lg font-semibold mb-4 text-gray-900">Remover Produto</h3>
            <p className="text-sm text-gray-600 mb-6">
              Tem certeza que deseja remover o produto <strong>{cancelProductData.name}</strong> desta venda? Esta ação não pode ser desfeita e retornará o item ao estoque.
            </p>

            <div className="flex justify-end gap-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  setIsCancelProductModalOpen(false);
                  setCancelProductData(null);
                }}
              >
                Voltar
              </Button>
              <Button 
                onClick={confirmCancelProduct} 
                className="bg-rose-600 hover:bg-rose-700 text-white"
              >
                Confirmar Remoção
              </Button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
